import { useEffect, useState } from "react";
import "./adminComponents.css";

// Recharts color palette — matches index.css design tokens.
export const CHART_COLORS = [
  "hsl(327, 72%, 42%)", // primary  (pink)
  "hsl(64, 53%, 46%)", // accent   (yellow-green)
  "hsl(181, 57%, 42%)", // success  (teal)
  "hsl(8, 75%, 58%)", // coral
  "hsl(327, 72%, 55%)", // primary lighter
  "hsl(220, 14%, 50%)", // muted slate
];

export function PageHeader({ title, description, actions }) {
  return (
    <header className="admin-pageHeader">
      <div className="admin-pageHeader-text">
        <h1 className="admin-pageTitle">{title}</h1>
        {description && <p className="admin-pageSubtitle">{description}</p>}
      </div>
      {actions && <div className="admin-pageHeader-actions">{actions}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ *
 * Card
 * ------------------------------------------------------------------ */
export function Card({
  title,
  description,
  actions,
  children,
  className = "",
}) {
  return (
    <article className={"admin-card " + className}>
      {(title || description || actions) && (
        <header className="admin-card-header">
          <div className="admin-card-header-text">
            {title && <h2 className="admin-card-title">{title}</h2>}
            {description && <p className="admin-card-desc">{description}</p>}
          </div>
          {actions && (
            <div className="admin-card-header-actions">{actions}</div>
          )}
        </header>
      )}
      <div className="admin-card-body">{children}</div>
    </article>
  );
}

/* ------------------------------------------------------------------ *
 * Stat tile
 * ------------------------------------------------------------------ */
export function StatCard({
  icon: Icon,
  title,
  value,
  description,
  trend,
  tone,
}) {
  const toneClass = tone ? ` admin-stat-tone-${tone}` : "";
  return (
    <div className={"admin-stat" + toneClass}>
      <div className="admin-stat-head">
        <span className="admin-stat-title">{title}</span>
        {Icon && <Icon className="admin-stat-icon" aria-hidden="true" />}
      </div>
      <div className="admin-stat-value">{value}</div>
      {(description || trend) && (
        <div className="admin-stat-meta">
          {trend && (
            <span
              className={
                "admin-stat-trend " +
                (trend.isPositive
                  ? "admin-stat-trend-up"
                  : "admin-stat-trend-down")
              }
            >
              {trend.isPositive ? "▲" : "▼"} {trend.value}%
            </span>
          )}
          {description && (
            <span className="admin-stat-desc">{description}</span>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Badge — tiny labeled pill. `tone` controls the colour:
 *   default | primary | success | warning | danger | muted | accent
 * ------------------------------------------------------------------ */
export function Badge({ tone = "default", children, className = "" }) {
  return (
    <span className={`admin-badge admin-badge-${tone} ${className}`}>
      {children}
    </span>
  );
}

// Map the 5 stages → badge tones for consistency.
export const STAGE_BADGE_TONES = {
  seedling: "muted",
  sprout: "accent",
  sapling: "success",
  tree: "primary",
  redwood: "danger",
};

export const RISK_BADGE_TONES = {
  low: "success",
  medium: "warning",
  high: "danger",
};

/* ------------------------------------------------------------------ *
 * Progress — labelled horizontal bar.
 * ------------------------------------------------------------------ */
export function Progress({ value = 0, tone = "primary" }) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="admin-progress">
      <div
        className={`admin-progress-bar admin-progress-${tone}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}


export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="admin-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          role="tab"
          aria-selected={value === tab.value}
          className={
            "admin-tab " + (value === tab.value ? "admin-tab-active" : "")
          }
          onClick={() => onChange(tab.value)}
          type="button"
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Toast — minimal notification surface that auto-dismisses.
 *
 * Pages call `useToast()` to get `{ toast, ToastViewport }`. Render
 * `<ToastViewport />` once at the bottom of the page.
 * ------------------------------------------------------------------ */
export function useToast() {
  const [items, setItems] = useState([]);

  const toast = (msg) => {
    const id = Math.random().toString(36).slice(2);
    const next = typeof msg === "string" ? { title: msg } : msg;
    setItems((current) => [...current, { id, ...next }]);
  };

  const remove = (id) =>
    setItems((current) => current.filter((item) => item.id !== id));

  function ToastViewport() {
    return (
      <div className="admin-toast-viewport" aria-live="polite">
        {items.map((item) => (
          <ToastItem
            key={item.id}
            item={item}
            onClose={() => remove(item.id)}
          />
        ))}
      </div>
    );
  }

  return { toast, ToastViewport };
}

function ToastItem({ item, onClose }) {
  useEffect(() => {
    const timeoutId = setTimeout(onClose, 3500);
    return () => clearTimeout(timeoutId);
  }, [onClose]);

  return (
    <div className={"admin-toast admin-toast-" + (item.tone || "default")}>
      {item.title && <div className="admin-toast-title">{item.title}</div>}
      {item.description && (
        <div className="admin-toast-desc">{item.description}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Date helpers (avoids a date-fns dependency).
 * ------------------------------------------------------------------ */
export function formatDate(
  value,
  opts = { month: "short", day: "numeric", year: "numeric" },
) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", opts);
}

export function daysBetween(start, end) {
  const startDate = start instanceof Date ? start : new Date(start);
  const endDate = end instanceof Date ? end : new Date(end);
  const ms = endDate - startDate;
  return Math.round(ms / 86400000);
}

export function subDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}
